import {type FormEvent, useCallback, useEffect, useState} from 'react';

import {useRouter} from 'next/router';
import {useTranslation} from 'react-i18next';

import {useAuth} from 'UI/hooks/useAuth';

/**
 * Hook für die Login-Seite. Kapselt State, Auth-Redirect und Submit-Logik.
 *
 * @returns Login-State und Handler.
 */
export const useLoginPage = () => {
    const {isLoading, login, user} = useAuth();
    const router = useRouter();
    const {t} = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const redirect = (router.query.redirect as string) || '/account';

    // Nur weiterleiten wenn ein Frontend-User eingeloggt ist (nicht bei reinem Admin-Login).
    useEffect(() => {
        if (!isLoading && user) {
            void router.replace(redirect);
        }
    }, [user, isLoading, router, redirect]);

    const handleSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            await login(email, password);
            await router.push(redirect);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('login.error'));
        } finally {
            setSubmitting(false);
        }
    }, [email, password, login, router, redirect, t]);

    const handleEmailChange = useCallback((e: FormEvent<HTMLInputElement>) => {
        setEmail(e.currentTarget.value);
    }, []);

    const handlePasswordChange = useCallback((e: FormEvent<HTMLInputElement>) => {
        setPassword(e.currentTarget.value);
    }, []);

    return {
        email,
        error,
        handleEmailChange,
        handlePasswordChange,
        handleSubmit,
        isLoading,
        password,
        registerHref: redirect === '/account'
            ? '/register'
            : `/register?redirect=${encodeURIComponent(redirect)}`,
        submitting,
        t,
        user
    };
};